import { ReviewSourceTypes } from "@/modules/agent/types";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Tooltip,
} from "@nextui-org/react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Maximize2,
  Minimize2,
  ZoomIn,
  ZoomOut
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { LoadingSpinner } from "../spin";

// Load pdfjs worker from CDN for the latest version (secure)
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
export type HighlightArea = {
  pageIndex: number;
  left: number;   // measured as percentage of page width (0-100)
  top: number;    // measured as percentage of page height (0-100)
  width: number;  // measured as percentage of page width (0-100)
  height: number; // measured as percentage of page height (0-100)
};

type PDFViewerProps = {
  fileUrl: string;
  isOpen: boolean;
  onClose: () => void;
  headerTitle: string;
  setSelectedSource: React.Dispatch<
    React.SetStateAction<ReviewSourceTypes | undefined>
  >;
  selectedSource?: ReviewSourceTypes;
  listOfSourcesForSelectedSources?: ReviewSourceTypes[];
  highlight?: HighlightArea[];
  pageNumber?: number;
};

const PDFLoader = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-90 z-50">
    <LoadingSpinner />
  </div>
);

const PDFError = () => (
  <div className="w-full bg-white flex h-full px-24">
    <div className="grow w-full flex flex-col items-start justify-center gap-4">
      <h4 className="text-4xl font-semibold leading-[60px]">
        Something went wrong loading the PDF
      </h4>
    </div>
  </div>
);

export default function PDFViewer({
  fileUrl,
  isOpen,
  highlight = [],
  headerTitle,
  pageNumber = 0,
  onClose,
  selectedSource,
  setSelectedSource,
  listOfSourcesForSelectedSources,
}: PDFViewerProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(
    pageNumber > 0 ? pageNumber : 1
  );
  const [scale, setScale] = useState<number>(1.0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // For overlay positioning and highlight ref
  const [pageInfo, setPageInfo] = useState<{
    viewport: any;
  } | null>(null);
  
  const [highlightRef, setHighlightRef] = useState<HTMLDivElement | null>(null);
  const [isScrollingProgrammatically, setIsScrollingProgrammatically] = useState(false);

  // Scroll listener to update current page based on scroll position
  useEffect(() => {
    const container = document.querySelector('[data-container="pdf-container"]');
    if (!container || numPages === 0) return;

    const handleScroll = () => {
      // Skip if we're in the middle of programmatic scrolling
      if (isScrollingProgrammatically) return;

      const containerRect = container.getBoundingClientRect();
      const toolbar = document.querySelector('[data-toolbar="pdf-toolbar"]');
      const toolbarHeight = toolbar ? toolbar.getBoundingClientRect().height : 60;
      
      // Find which page is most visible in the viewport
      let visiblePage = 1;
      let maxVisibleArea = 0;

      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        const pageElement = document.querySelector(`[data-page-number="${pageNum}"]`);
        if (pageElement) {
          const pageRect = pageElement.getBoundingClientRect();
          
          // Calculate visible area of this page
          const visibleTop = Math.max(pageRect.top, containerRect.top + toolbarHeight);
          const visibleBottom = Math.min(pageRect.bottom, containerRect.bottom);
          const visibleHeight = Math.max(0, visibleBottom - visibleTop);
          const visibleArea = visibleHeight * pageRect.width;

          if (visibleArea > maxVisibleArea) {
            maxVisibleArea = visibleArea;
            visiblePage = pageNum;
          }
        }
      }

      if (visiblePage !== currentPage) {
        setCurrentPage(visiblePage);
      }
    };

    container.addEventListener('scroll', handleScroll);
    
    // Initial check
    handleScroll();

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [numPages, currentPage, isScrollingProgrammatically]);

  // Update current page if prop changes and scroll to it
  useEffect(() => {
    if (pageNumber && pageNumber !== currentPage) {
      setIsScrollingProgrammatically(true);
      setCurrentPage(pageNumber);
      // Scroll to highlight after a brief delay to ensure it's rendered
      setTimeout(() => {
        if (highlightRef) {
          const container = document.querySelector('[data-container="pdf-container"]');
          if (container && highlightRef) {
            const containerRect = container.getBoundingClientRect();
            const highlightRect = highlightRef.getBoundingClientRect();
            const toolbar = document.querySelector('[data-toolbar="pdf-toolbar"]');
            const toolbarHeight = toolbar ? toolbar.getBoundingClientRect().height : 60;
            
            // Calculate the scroll position to place highlight at top, just below toolbar
            const scrollTop = highlightRect.top - containerRect.top + container.scrollTop - toolbarHeight - 10;
            
            container.scrollTo({
              top: scrollTop,
              behavior: 'smooth'
            });
          } else {
            highlightRef.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }
        // Reset the flag after scrolling is complete
        setTimeout(() => setIsScrollingProgrammatically(false), 800);
      }, 100);
    }
  }, [pageNumber, highlightRef]);

  // Scroll to current highlight when selectedSource changes
  useEffect(() => {
    if (selectedSource && highlightRef) {
      setIsScrollingProgrammatically(true);
      setTimeout(() => {
        const container = document.querySelector('[data-container="pdf-container"]');
        if (container && highlightRef) {
          const containerRect = container.getBoundingClientRect();
          const highlightRect = highlightRef.getBoundingClientRect();
          const toolbar = document.querySelector('[data-toolbar="pdf-toolbar"]');
          const toolbarHeight = toolbar ? toolbar.getBoundingClientRect().height : 60;
          
          // Calculate the scroll position to place highlight at top, just below toolbar
          const scrollTop = highlightRect.top - containerRect.top + container.scrollTop - toolbarHeight - 10; // 10px padding below toolbar
          
          container.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
          });
        } else {
          highlightRef.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
        // Reset the flag after scrolling is complete
        setTimeout(() => setIsScrollingProgrammatically(false), 800);
      }, 100);
    }
  }, [selectedSource, highlightRef]);

  // Find current reference index
  const findindex = useMemo(() => {
    if (listOfSourcesForSelectedSources) {
      return (
        listOfSourcesForSelectedSources.findIndex(
          (d) => d.source_number === selectedSource?.source_number
        ) + 1
      );
    }
    return 0;
  }, [listOfSourcesForSelectedSources, selectedSource]);

  const isNextAvailable = useMemo(() => {
    if (listOfSourcesForSelectedSources) {
      const findindex = listOfSourcesForSelectedSources.findIndex(
        (d) => d.source_number === selectedSource?.source_number
      );
      return findindex < listOfSourcesForSelectedSources.length - 1;
    }
    return false;
  }, [listOfSourcesForSelectedSources, selectedSource]);

  const handleNext = useCallback(() => {
    if (listOfSourcesForSelectedSources && isNextAvailable) {
      const findindex = listOfSourcesForSelectedSources.findIndex(
        (d) => d.source_number === selectedSource?.source_number
      );
      setSelectedSource(listOfSourcesForSelectedSources[findindex + 1]);
    }
  }, [
    listOfSourcesForSelectedSources,
    setSelectedSource,
    isNextAvailable,
    selectedSource,
  ]);

  const isPreviousAvailable = useMemo(() => {
    if (listOfSourcesForSelectedSources) {
      const findindex = listOfSourcesForSelectedSources.findIndex(
        (d) => d.source_number === selectedSource?.source_number
      );
      return findindex > 0;
    }
    return false;
  }, [listOfSourcesForSelectedSources, selectedSource]);

  const handlePrevious = useCallback(() => {
    if (listOfSourcesForSelectedSources && isPreviousAvailable) {
      const findindex = listOfSourcesForSelectedSources.findIndex(
        (d) => d.source_number === selectedSource?.source_number
      );
      setSelectedSource(listOfSourcesForSelectedSources[findindex - 1]);
    }
  }, [
    listOfSourcesForSelectedSources,
    setSelectedSource,
    isPreviousAvailable,
    selectedSource,
  ]);

  // Toolbar actions
  const handleZoomIn = () => setScale((s) => Math.min(s + 0.2, 3));
  const handleZoomOut = () => setScale((s) => Math.max(s - 0.2, 0.5));
  const handleFullScreen = () => setIsFullscreen((f) => !f);

  // Page navigation
  const goToPage = (n: number) => {
    if (n >= 1 && n <= numPages) {
      setIsScrollingProgrammatically(true);
      setCurrentPage(n);
      // Scroll to the specific page
      setTimeout(() => {
        const pageElement = document.querySelector(`[data-page-number="${n}"]`);
        if (pageElement) {
          pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        // Reset the flag after scrolling is complete
        setTimeout(() => setIsScrollingProgrammatically(false), 800);
      }, 50);
    }
  };

  // When the page renders, get viewport for coordinate transformation
  const onPageRenderSuccess = useCallback((page: any) => {
    const viewport = page.getViewport({ scale });
    setPageInfo({ viewport });
  }, [scale]);

  // Utility: Simple highlight positioning using percentage-based coordinates
  const getHighlightStyle = (area: HighlightArea) => {
    if (!pageInfo) return { display: "none" };
    
    // Use percentage-based positioning which is more reliable
    return {
      position: "absolute" as const,
      left: `${area.left}%`,
      top: `${area.top}%`,
      width: `${area.width}%`,
      height: `${area.height}%`,
      background: "rgba(255, 255, 0, 0.4)",
      border: "1px solid rgba(255, 255, 0, 0.8)",
      pointerEvents: "none" as const,
      zIndex: 5,
      boxSizing: "border-box" as const,
    };
  };

  // Modal full screen styling
  const modalClass = isFullscreen
    ? "fixed inset-0 z-[9999] bg-black bg-opacity-90 rounded-none"
    : "max-h-[calc(100vh-32px)] max-w-[calc(100vw-200px)] !rounded-[8px] overflow-y-hidden !my-auto";

  return (
    <Modal
      className={modalClass}
      style={{
        borderRadius: "8px !important",
      }}
      size="full"
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalContent className="flex flex-col h-full">
        {() => (
          <>
            <ModalHeader className="border-b border-solid border-[#E4E4E7] flex-shrink-0">
              <div className="flex gap-3 items-center ">
                <div className="flex gap-1 items-center pr-3 border-r border-[#E4E4E7] border-solid">
                  <Tooltip
                    content="Previous Reference"
                    classNames={{
                      content:
                        "bg-black bg-opacity-80 backdrop-blur-md text-white text-tiny",
                      arrow: "bg-black bg-opacity-80 backdrop-blur-md",
                    }}
                  >
                    <div
                      className="select-none bg-white cursor-pointer border-[1px] h-5 w-5 flex items-center justify-center rounded-[50%] border-solid border-[#E4E4E7]"
                      onClick={handlePrevious}
                    >
                      <ChevronLeft
                        color={isPreviousAvailable ? "#A1A1AA" : "#E4E4E7"}
                        size={16}
                      />
                    </div>
                  </Tooltip>
                  <div className="text-[12px] leading-5 font-medium text-[#71717A] w-11 text-center">
                    {findindex} of {listOfSourcesForSelectedSources?.length}
                  </div>
                  <Tooltip
                    content="Next Reference"
                    classNames={{
                      content:
                        "bg-black bg-opacity-80 backdrop-blur-md text-white text-tiny",
                      arrow: "bg-black bg-opacity-80 backdrop-blur-md",
                    }}
                  >
                    <div
                      className="select-none bg-white cursor-pointer border-[1px] h-5 w-5 flex items-center justify-center rounded-[50%] border-solid border-[#E4E4E7]"
                      onClick={handleNext}
                    >
                      <ChevronRight
                        color={isNextAvailable ? "#A1A1AA" : "#E4E4E7"}
                        size={16}
                      />
                    </div>
                  </Tooltip>
                </div>
                <p className="text-sm font-medium">{headerTitle}</p>
              </div>
            </ModalHeader>
            <ModalBody className="flex-1 overflow-auto px-0 py-0 min-h-0">
              <div
                data-container="pdf-container"
                style={{
                  overflowY: "auto",
                  background: "#f6f6f7",
                  position: "relative",
                  height: "100%",
                  width: "100%",
                }}
              >
                {/* Custom Toolbar */}
                <div
                  data-toolbar="pdf-toolbar"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 16px",
                    background: "#fff",
                    borderBottom: "1px solid #e4e4e7",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
                  }}
                >
                  {/* Page navigation */}
                  <div style={{ display: "flex", alignItems: "center", gap: 4, flex: 1 }}>
                    <Tooltip content="First page">
                      <button
                        onClick={() => goToPage(1)}
                        disabled={currentPage === 1}
                        className="p-1 rounded-lg bg-white  hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronsLeft size={16} className="text-gray-600" />
                      </button>
                    </Tooltip>
                    <Tooltip content="Previous page">
                      <button
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage <= 1}
                        className="p-1 rounded-lg bg-white  hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeft size={16} className="text-gray-600" />
                      </button>
                    </Tooltip>
                    
                    <div className="flex items-center gap-2 mx-2">
                      <span className="text-sm font-medium text-gray-700">Page</span>
                      <input
                        type="number"
                        value={currentPage}
                        min={1}
                        max={numPages}
                        className="w-16 p-1 text-sm text-center border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        onChange={(e) =>
                          goToPage(Number(e.target.value) || currentPage)
                        }
                      />
                      <span className="text-sm text-gray-500">of {numPages || "?"}</span>
                    </div>

                    <Tooltip content="Next page">
                      <button
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage >= numPages}
                        className="p-1 rounded-lg bg-white  hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronRight size={16} className="text-gray-600" />
                      </button>
                    </Tooltip>
                    <Tooltip content="Last page">
                      <button
                        onClick={() => goToPage(numPages)}
                        disabled={currentPage === numPages}
                        className="p-1 rounded-lg bg-white  hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronsRight size={16} className="text-gray-600" />
                      </button>
                    </Tooltip>
                  </div>

                  {/* Zoom Controls - Centered */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", flex: 1 }}>
                    <Tooltip content="Zoom out">
                      <button
                        onClick={handleZoomOut}
                        disabled={scale <= 0.5}
                        className="p-1 rounded-lg bg-white  hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ZoomOut size={16} className="text-gray-600" />
                      </button>
                    </Tooltip>
                    <div className="px-3 py-0 bg-gray-50 rounded-md ">
                      <span className="text-sm font-medium text-gray-700">
                        {Math.round(scale * 100)}%
                      </span>
                    </div>
                    <Tooltip content="Zoom in">
                      <button
                        onClick={handleZoomIn}
                        disabled={scale >= 3}
                        className="p-1 rounded-lg bg-white  hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ZoomIn size={16} className="text-gray-600" />
                      </button>
                    </Tooltip>
                  </div>

                  {/* Fullscreen */}
                  <div style={{ display: "flex", justifyContent: "flex-end", flex: 1 }}>
                    <Tooltip content={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}>
                      <button
                        onClick={handleFullScreen}
                        className="p-1 rounded-lg bg-white  hover:bg-gray-50 transition-colors"
                      >
                        {isFullscreen ? (
                          <Minimize2 size={16} className="text-gray-600" />
                        ) : (
                          <Maximize2 size={16} className="text-gray-600" />
                        )}
                      </button>
                    </Tooltip>
                  </div>
                </div>
                <Document
                  file={fileUrl}
                  loading={<PDFLoader />}
                  error={PDFError}
                  onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                  onLoadError={PDFError}
                  className="text-center"
                >
                  <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", gap: "20px", padding: "20px 0 0 0" }}>
                    {/* Render all pages */}
                    {Array.from(new Array(numPages), (el, index) => (
                      <div 
                        key={`page_${index + 1}`} 
                        data-page-number={index + 1}
                        style={{ position: "relative", display: "inline-block" }}
                      >
                        <Page
                          pageNumber={index + 1}
                          scale={scale}
                          width={isFullscreen ? undefined : 800}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                          loading=""
                          onRenderSuccess={(page) => {
                            // Only update pageInfo for the current page or first page
                            if (index + 1 === currentPage || (currentPage === 1 && index === 0)) {
                              onPageRenderSuccess(page);
                            }
                          }}
                        />
                        {/* highlights for this specific page */}
                        {pageInfo &&
                          highlight
                            .filter((h) => h.pageIndex === index)
                            .sort((a, b) => a.top - b.top) // Sort by top position (topmost first)
                            .map((area, idx) => {
                              // global index of this highlight in the original array
                              const globalIndex = highlight.findIndex(h => 
                                h.pageIndex === area.pageIndex && 
                                h.left === area.left && 
                                h.top === area.top &&
                                h.width === area.width &&
                                h.height === area.height
                              );
                              
                              const isTargetPage = pageNumber ? index === pageNumber - 1 : index === 0;

                              // After sorting, idx === 0 will be the topmost highlight
                              const isFirstHighlightOnTargetPage = isTargetPage && idx === 0;
                              
                              const shouldSetRef = isFirstHighlightOnTargetPage;
                              
                              return (
                                <div
                                  key={`${globalIndex}-${idx}`}
                                  ref={shouldSetRef ? setHighlightRef : undefined}
                                  className="highlight-area"
                                  data-highlight={`page-${area.pageIndex}-${globalIndex}`}
                                  style={getHighlightStyle(area)}
                                />
                              );
                            })}
                      </div>
                    ))}
                  </div>
                </Document>
              </div>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}